import { LessonDefinition } from "../../types";

const lesson: LessonDefinition = {
  id: "department-preferences-templates-checklists",
  courseId: "for-agency-admins",
  order: 3,
  title: "Department defaults",
  subtitle: "Preferences, templates, checklists",
  summary:
    "To make every report read the same way, push preferences to all officers, publish department templates and snippets, and build checklists.",
  learn: [
    "Push a preference set to every officer",
    "Create department templates and set a default",
    "Build a checklist template with required items",
  ],
  minutes: 5,
  releasedAt: "2025-06-01T00:00:00.000Z",
  gate: "admin",
  body: [
    { type: "heading", text: "Report Preferences" },
    {
      type: "steps",
      items: [
        "Open the **Report Preferences** tab. The fields match an officer's /preferences page.",
        "Set the department standard (perspective, time and date format, rank style, call sign format and placement, headings).",
        "Click **Save Agency Preferences**.",
        "Use the push action and confirm in \"Confirm Push Preferences\".",
      ],
    },
    {
      type: "paragraph",
      text: "A push overwrites every officer's current values. Officers can still change fields afterward, so push again to reset.",
    },
    {
      type: "callout",
      tone: "note",
      text: "A push includes the call sign format and placement but never an officer's call sign itself. That stays personal.",
    },
    { type: "heading", text: "Templates and snippets" },
    {
      type: "steps",
      items: [
        "Lower on the same tab, create department report templates (header, writing instructions, footer). Officers see them with a \"Dept\" badge and cannot edit them.",
        "Click **Set as default** on one. It shows \"Default for all\" and is pre-selected on every officer's uploads.",
        "On the **Snippets** tab, click **Add Snippet** to publish \"Department Snippets\" to every editor. **Delete Snippet** removes one.",
        "On the **Form Templates** tab, click **Upload Template** with agency scope to share a PDF form with everyone.",
      ],
    },
    { type: "heading", text: "Checklists" },
    {
      type: "steps",
      items: [
        "Open **Checklists** (\"Checklist Template Builder\").",
        "Start with **Use Sample Template** or **Create New Template**.",
        "Click **Add Category**, then **Add Item** under it.",
        "Mark items Required, and turn on Enable Additional Notes where a short answer is needed.",
        "Click **Save Template**.",
      ],
    },
    {
      type: "paragraph",
      text: "Officers now see **Checklist** in their **New Case** menu.",
    },
    {
      type: "callout",
      tone: "tip",
      title: "Split facts from structure",
      text: "Put facts the AI cannot know (case number, CAD event) as placeholders in the template header, and structure in the writing instructions. Officers then fill the brackets instead of fighting the format.",
    },
    {
      type: "paragraph",
      text: "The officer-side lessons on report templates, checklists, and snippets show what these look like once published.",
    },
  ],
  related: ["report-templates", "checklists", "snippets"],
};

export default lesson;
